import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Plane, Home as HomeIcon, FileText, Settings, PlusCircle, Users as UsersIcon, LogOut } from "lucide-react";
import { Button } from "../ui/button";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const NavItem = ({ to, icon: Icon, children, testId, end, badge }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) => `side-item ${isActive ? "active" : ""}`}
  >
    <Icon size={18} strokeWidth={2} />
    <span className="flex-1">{children}</span>
    {badge > 0 && (
      <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold" data-testid="nav-badge-unread">
        {badge}
      </span>
    )}
  </NavLink>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const fetchStats = () => {
      http.get("/quotes/stats").then((r) => setUnread(r.data.unread_acceptances || 0)).catch(() => {});
    };
    fetchStats();
    const t = setInterval(fetchStats, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <aside
      className="hidden lg:flex flex-col w-64 shrink-0 min-h-screen sticky top-0 px-4 py-6"
      style={{ background: "var(--sidebar)" }}
    >
      <div className="flex items-center gap-2 px-3 mb-8">
        <div className="w-9 h-9 rounded-xl bg-sky-600 flex items-center justify-center">
          <Plane className="text-white" size={18} strokeWidth={2.4} />
        </div>
        <div>
          <div className="text-white font-bold tracking-tight leading-tight">Voyager</div>
          <div className="text-[11px] uppercase tracking-widest text-sky-300">Cotizador</div>
        </div>
      </div>

      <Button
        data-testid="nav-button-new-quote"
        onClick={() => navigate("/cotizacion/nueva")}
        className="mb-6 bg-sky-600 hover:bg-sky-700 text-white rounded-xl h-11 font-semibold shadow-lg shadow-sky-950/40"
      >
        <PlusCircle size={18} className="mr-2" /> Nueva cotización
      </Button>

      <nav className="flex flex-col gap-1">
        <NavItem to="/" icon={HomeIcon} testId="nav-link-home" end badge={unread}>Historial</NavItem>
        <NavItem to="/cotizacion/nueva" icon={FileText} testId="nav-link-new">Crear</NavItem>
        <NavItem to="/config" icon={Settings} testId="nav-button-settings-logo">Configuración</NavItem>
        {user?.role === "admin" && (
          <NavItem to="/usuarios" icon={UsersIcon} testId="nav-link-users">Usuarios</NavItem>
        )}
      </nav>

      <div className="mt-auto pt-6 border-t border-white/10 space-y-3">
        {user && (
          <div className="flex items-center gap-2 px-1" data-testid="sidebar-user">
            <div className="w-9 h-9 rounded-full bg-sky-600/20 border border-sky-500/40 flex items-center justify-center text-white text-xs font-bold">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">{user.name || user.email.split("@")[0]}</p>
              <p className="text-[10px] uppercase tracking-widest text-sky-300 truncate">{user.role}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={logout} className="text-slate-300 hover:text-white hover:bg-white/10 h-8 px-2" data-testid="button-logout" title="Salir">
              <LogOut size={14} />
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
